import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Plus, Server, Globe, Pencil, Trash2, X, ShieldCheck, ShieldAlert, Check } from 'lucide-react';
import { useMonitors, Monitor, MonitorStatus } from '../../hooks/useMonitors';
import { useApi } from '../../hooks/useApi';

interface ClientOption { id: string; company_name: string; logo_url: string | null }

const DOT: Record<MonitorStatus, string> = {
  up: 'bg-emerald-500',
  warn: 'bg-amber-500',
  down: 'bg-red-500',
  unknown: 'bg-slate-400',
};
const STATUS_LABEL: Record<MonitorStatus, string> = { up: 'Online', warn: 'Attenzione', down: 'Offline', unknown: '—' };

function relTime(iso: string | null): string {
  if (!iso) return 'mai';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'ora';
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return `${Math.floor(s / 86400)} g fa`;
}

function SslBadge({ m }: { m: Monitor }) {
  if (!m.check_ssl || m.kind !== 'project') return null;
  const s = m.ssl_status;
  if (s === 'ok') return <Badge color="green" icon={<ShieldCheck className="h-3 w-3" />}>SSL {m.ssl_days_left}g</Badge>;
  if (s === 'expiring') return <Badge color="amber" icon={<ShieldAlert className="h-3 w-3" />}>SSL {m.ssl_days_left}g</Badge>;
  if (s === 'expired') return <Badge color="red" icon={<ShieldAlert className="h-3 w-3" />}>SSL scaduto</Badge>;
  if (s === 'invalid') return <Badge color="red" icon={<ShieldAlert className="h-3 w-3" />}>SSL invalido</Badge>;
  if (s === 'none') return <Badge color="slate">no HTTPS</Badge>;
  return null;
}

function Badge({ children, color, icon }: { children: React.ReactNode; color: 'green' | 'amber' | 'red' | 'slate' | 'blue'; icon?: React.ReactNode }) {
  const styles = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    slate: 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-slate-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  }[color];
  return <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${styles}`}>{icon}{children}</span>;
}

function TrackBadge({ label, val }: { label: string; val: boolean | null }) {
  if (val === null) return null;
  return val
    ? <Badge color="green" icon={<Check className="h-3 w-3" />}>{label}</Badge>
    : <Badge color="red" icon={<X className="h-3 w-3" />}>{label}</Badge>;
}

function MonitorCard({ m, onCheck, onEdit, onDelete }: { m: Monitor; onCheck: () => void; onEdit: () => void; onDelete: () => void }) {
  const [busy, setBusy] = useState(false);
  const target = m.kind === 'server' ? `${m.host}:${m.port || 443}` : (() => { try { return new URL(m.url || '').hostname; } catch { return m.url; } })();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${DOT[m.status]}`} title={STATUS_LABEL[m.status]} />
          {m.kind === 'server' ? <Server className="h-4 w-4 flex-shrink-0 text-slate-400" /> : <Globe className="h-4 w-4 flex-shrink-0 text-slate-400" />}
          <span className="truncate font-semibold text-slate-800 dark:text-slate-100">{m.name}</span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button onClick={async () => { setBusy(true); await onCheck(); setBusy(false); }} title="Controlla ora" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-gray-800">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onEdit} title="Modifica" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-gray-800"><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={onDelete} title="Elimina" className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {(m.client_logo || m.client_name) && (
        <div className="mt-2 flex items-center gap-1.5">
          {m.client_logo && <img src={m.client_logo} alt="" className="h-4 w-4 rounded object-cover" />}
          <span className="truncate text-xs text-slate-400">{m.client_name}</span>
        </div>
      )}

      <div className="mt-1 truncate text-xs text-slate-400">{target}</div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <span className={`text-xs font-semibold ${m.status === 'up' ? 'text-emerald-600' : m.status === 'warn' ? 'text-amber-600' : m.status === 'down' ? 'text-red-600' : 'text-slate-400'}`}>
          {STATUS_LABEL[m.status]}
        </span>
        {m.latency_ms != null && m.status !== 'down' && <span className="text-xs text-slate-400">· {m.latency_ms} ms</span>}
        {m.http_code != null && <Badge color="slate">HTTP {m.http_code}</Badge>}
        <SslBadge m={m} />
        <TrackBadge label="Meta" val={m.check_meta_pixel && m.kind === 'project' ? m.has_meta_pixel : null} />
        <TrackBadge label="Google" val={m.check_google && m.kind === 'project' ? m.has_google : null} />
      </div>

      {m.error && m.status === 'down' && <div className="mt-2 truncate text-[11px] text-red-500">{m.error}</div>}

      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-[11px] text-slate-400 dark:border-gray-800">
        <span>uptime 24h: {m.uptime_24h != null ? `${m.uptime_24h}%` : '—'}</span>
        <span>controllato {relTime(m.last_checked_at)}</span>
      </div>
    </div>
  );
}

interface FormState {
  id?: string;
  name: string;
  kind: 'server' | 'project';
  url: string;
  host: string;
  port: string;
  client_id: string | null;
  check_ssl: boolean;
  check_meta_pixel: boolean;
  check_google: boolean;
}

function MonitorModal({ initial, clients, onClose, onSave }: { initial: FormState; clients: ClientOption[]; onClose: () => void; onSave: (f: FormState) => Promise<void> }) {
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const valid = form.name.trim() && (form.kind === 'server' ? form.host.trim() : form.url.trim());
  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try { await onSave(form); onClose(); } catch { /* toast nel hook */ } finally { setSaving(false); }
  };
  const inp = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100';
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-8" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{initial.id ? 'Modifica monitor' : 'Nuovo monitor'}</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {(['project', 'server'] as const).map((k) => (
              <button key={k} onClick={() => setForm({ ...form, kind: k })}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm ${form.kind === k ? 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-gray-900' : 'border-slate-300 text-slate-600 dark:border-gray-700 dark:text-slate-300'}`}>
                {k === 'server' ? <Server className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                {k === 'server' ? 'Server' : 'Progetto/Sito'}
              </button>
            ))}
          </div>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome (es. Sito Roberto)" className={inp} autoFocus />

          {form.kind === 'project' ? (
            <>
              <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://esempio.com" className={inp} />
              <div className="space-y-1.5 rounded-lg bg-slate-50 p-3 dark:bg-gray-800/50">
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={form.check_ssl} onChange={(e) => setForm({ ...form, check_ssl: e.target.checked })} /> Controlla certificato SSL
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={form.check_meta_pixel} onChange={(e) => setForm({ ...form, check_meta_pixel: e.target.checked })} /> Controlla pixel Meta
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={form.check_google} onChange={(e) => setForm({ ...form, check_google: e.target.checked })} /> Controlla tracking Google
                </label>
              </div>
            </>
          ) : (
            <div className="flex gap-2">
              <input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="IP o dominio" className={`${inp} flex-1`} />
              <input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder="Porta (443)" className={`${inp} w-28`} inputMode="numeric" />
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Cliente (opzionale)</label>
            <select value={form.client_id || ''} onChange={(e) => setForm({ ...form, client_id: e.target.value || null })} className={inp}>
              <option value="">Nessuno</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-800">Annulla</button>
          <button onClick={save} disabled={!valid || saving} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-gray-900">{saving ? 'Salvo…' : 'Salva'}</button>
        </div>
      </div>
    </div>
  );
}

const emptyForm = (): FormState => ({ name: '', kind: 'project', url: '', host: '', port: '', client_id: null, check_ssl: true, check_meta_pixel: true, check_google: true });

const StatoBoard: React.FC = () => {
  const { monitors, loading, error, checking, createMonitor, updateMonitor, deleteMonitor, checkOne, checkAll } = useMonitors();
  const api = useApi();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [modal, setModal] = useState<FormState | null>(null);

  useEffect(() => {
    api('/clients?limit=500').then((d) => setClients((Array.isArray(d) ? d : []).map((c: any) => ({ id: c.id, company_name: c.company_name, logo_url: c.logo_url || null })))).catch(() => setClients([]));
  }, [api]);

  const counts = useMemo(() => ({
    up: monitors.filter((m) => m.status === 'up').length,
    warn: monitors.filter((m) => m.status === 'warn').length,
    down: monitors.filter((m) => m.status === 'down').length,
  }), [monitors]);

  const toForm = (m: Monitor): FormState => ({
    id: m.id, name: m.name, kind: m.kind, url: m.url || '', host: m.host || '', port: m.port ? String(m.port) : '',
    client_id: m.client_id, check_ssl: m.check_ssl, check_meta_pixel: m.check_meta_pixel, check_google: m.check_google,
  });

  const save = async (f: FormState) => {
    const payload: any = {
      name: f.name, kind: f.kind, client_id: f.client_id,
      url: f.kind === 'project' ? f.url : null,
      host: f.kind === 'server' ? f.host : null,
      port: f.kind === 'server' ? (parseInt(f.port, 10) || 443) : null,
      check_ssl: f.check_ssl, check_meta_pixel: f.check_meta_pixel, check_google: f.check_google,
    };
    if (f.id) await updateMonitor(f.id, payload);
    else await createMonitor(payload);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> {counts.up} online</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> {counts.warn} attenzione</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> {counts.down} offline</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={checkAll} disabled={checking} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800">
            <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} /> Controlla tutto
          </button>
          <button onClick={() => setModal(emptyForm())} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-white dark:text-gray-900">
            <Plus className="h-4 w-4" /> Aggiungi
          </button>
        </div>
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</div>}

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Caricamento…</div>
      ) : monitors.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center dark:border-gray-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">Nessun monitor. Aggiungi un server o un progetto per iniziare.</p>
          <button onClick={() => setModal(emptyForm())} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white dark:bg-white dark:text-gray-900"><Plus className="h-4 w-4" /> Aggiungi il primo</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {monitors.map((m) => (
            <MonitorCard key={m.id} m={m} onCheck={() => checkOne(m.id)} onEdit={() => setModal(toForm(m))} onDelete={() => { if (confirm(`Eliminare il monitor "${m.name}"?`)) deleteMonitor(m.id); }} />
          ))}
        </div>
      )}

      {modal && <MonitorModal initial={modal} clients={clients} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
};

export default StatoBoard;
